import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import { setAlert } from "../../../../actions/alert";
import {
  getCreationEventContext,
  clearCreationEventContext,
} from "../../../../util/creationEventContext";
import CheckUnauth from "../../../auth/CheckUnauth";
import RestaurantEditForm from "../restaurantBuilderLegacy/RestaurantEditForm";
import { slugify } from "../../../../util/slugify";

const PageContainer = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
  padding: 24px 16px 48px;
`;

const PageHeader = styled.div`
  max-width: 800px;
  margin: 0 auto 24px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    color: #4caf50;
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #757575;
  margin: 0;
`;

const ContentCard = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const RestaurantMenuManual = ({ setAlert }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() => {
    if (location.state) {
      const { name, address, geoJson, status, website, googleId } = location.state;
      return { name, address, geoJson, status, website, googleId };
    }
    return {};
  });

  const handleSubmit = async (restaurant) => {
    const hasLocation = formData.geoJson?.coordinates?.length === 2;

    if (hasLocation) {
      // We have coordinates — create the restaurant directly
      const eventCtx = getCreationEventContext();
      const restaurantData = {
        name: restaurant.name,
        location: `${formData.geoJson.coordinates[1]},${formData.geoJson.coordinates[0]}`,
        cuisine: restaurant.cuisine,
        veganOptions: restaurant.veganOptions,
        metadata: { isGoogle: false },
        status: formData.status || restaurant.status || "RESTAURANT",
        website: restaurant.website || formData.website || null,
        googleId: formData.googleId || null,
        ...(restaurant.address && {
          address: { formatted_address: restaurant.address },
        }),
        ...(eventCtx?.eventId && { eventId: eventCtx.eventId, eventOnly: true }),
      };

      const config = {
        headers: { "Content-Type": "application/json" },
      };

      try {
        const response = await axios.post(
          "/api/restaurants",
          JSON.stringify(restaurantData),
          config
        );
        setAlert("Restaurant successfully created", "success");
        if (eventCtx?.returnTo) {
          clearCreationEventContext();
          navigate(eventCtx.returnTo);
        } else {
          navigate(`/details/${response.data._id}/${slugify(response.data.name)}`);
        }
      } catch (err) {
        setAlert(
          err.response?.data?.errors
            ? err.response.data.errors[0].msg
            : "Error creating restaurant, please try again",
          "failure"
        );
      }
    } else {
      // No coordinates — navigate to the address step
      navigate("/create/v2/address", {
        state: {
          name: restaurant.name,
          cuisine: restaurant.cuisine,
          veganOptions: restaurant.veganOptions,
          status: formData.status || restaurant.status || "RESTAURANT",
          website: restaurant.website || formData.website || null,
          googleId: formData.googleId || null,
          address: restaurant.address || formData.address || null,
          geoJson: formData.geoJson || null,
        },
      });
    }
  };

  return (
    <PageContainer>
      <CheckUnauth {...location} />
      <PageHeader>
        <PageTitle>
          <i className="fas fa-plus-circle" />
          Create a Restaurant
        </PageTitle>
        <PageSubtitle>
          Add vegan menu items, assign sections, and pick a cuisine
        </PageSubtitle>
      </PageHeader>
      <ContentCard>
        <RestaurantEditForm
          currRestaurant={{
            name: formData.name || "",
            coordinates: formData.geoJson?.coordinates
              ? `${formData.geoJson.coordinates[1]},${formData.geoJson.coordinates[0]}`
              : "",
            metadata: { isGoogle: false },
            status: formData.status || "RESTAURANT",
            googleId: formData.googleId || null,
            address: formData.address || null,
          }}
          submitBackendCall={handleSubmit}
          displayText={
            formData.geoJson?.coordinates
              ? "Create Restaurant"
              : "Continue to Location"
          }
          setAlert={setAlert}
        />
      </ContentCard>
    </PageContainer>
  );
};

export default connect(null, { setAlert })(RestaurantMenuManual);
